import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectIsBrandAffinityFeatureFlagOn } from 'core/userContext';
import { BrandAffinityLevel } from 'modules/domains/brandAffinity';
import { BrandAffinityIndicator } from 'ui/brandAffinityIndicator';
import { Skeleton } from 'ui/skeleton';
import { SlideOver, XAIHeader } from 'ui/slideOver';
import { colors, fontSizes, spacing } from 'ui/theme';
import { ExpandModes, useExpansionContext } from 'utils/contexts/expansion';
import { pxToRem } from 'utils/convert';

import { useUserMetadataQueryContext } from '../../contexts/UserMetadataQueryContext';
import { useUserDisplayName } from '../../hooks';
import { XAIContainer } from './BrandAffinityXAI';

const displayText = {
  displayNamePlaceholder: 'No email available for user.',
  slideOverHeader: 'Understand user affinity',
};

const Wrapper = styled.li`
  display: flex;
  flex-direction: column;
  row-gap: ${spacing.threeQuarter};
  margin-bottom: ${spacing.quarter};
  max-width: 100%;

  & > div {
    max-width: ${pxToRem(260)};
    justify-content: space-evenly;
  }
`;

const TitleText = styled.span<{ $isPlaceholder: boolean }>`
  font-size: ${fontSizes.s24};
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? colors.textSecondary : colors.textPrimary)};
  margin-right: ${spacing.threeQuarter};
  flex-basis: fit-content;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const BrandAffinityLoadingPlaceholder = styled(Skeleton)`
  display: inline-block;
  background-color: ${colors.grey5};
  border-radius: ${spacing.base};
  height: ${pxToRem(26)};
  width: ${spacing.x10};
`;

const TitleTextLoadingPlaceholder = styled(Skeleton)`
  display: inline-block;
  background-color: ${colors.grey5};
  height: ${pxToRem(36)};
  width: ${spacing.x16};
  margin-right: ${spacing.threeQuarter};
`;

export const Title = () => {
  const { collapse, expand, mode } = useExpansionContext();
  const { brandAffinityLevel, isLoading } = useUserMetadataQueryContext();
  const isBrandAffinityFeatureEnabled = useSelector(selectIsBrandAffinityFeatureFlagOn);
  const userDisplayName = useUserDisplayName(false);

  useEffect(() => {
    collapse();
    return collapse;
  }, []);

  if (isLoading) {
    return (
      <Wrapper>
        <TitleTextLoadingPlaceholder />
        {isBrandAffinityFeatureEnabled && <BrandAffinityLoadingPlaceholder />}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {isBrandAffinityFeatureEnabled && (
        <>
          <BrandAffinityIndicator
            level={brandAffinityLevel || BrandAffinityLevel.UNSCORED}
            setXAIStatus={expand}
            withXAI={true}
          />
          <SlideOver
            id="brand-affinity-xai"
            isOpen={mode === ExpandModes.expand}
            onClose={collapse}
            headerContent={<XAIHeader>{displayText.slideOverHeader}</XAIHeader>}
            bodyContent={<XAIContainer />}
            onClickOutside={collapse}
          />
        </>
      )}
      <TitleText title={userDisplayName} $isPlaceholder={!userDisplayName}>
        {userDisplayName || displayText.displayNamePlaceholder}
      </TitleText>
    </Wrapper>
  );
};
